import { signal, useSignalValue } from "../../../../../utils/signal";
import React, { useEffect, useState } from "react";
import { getRequest, postRequest } from "../../../../../services/apiService";

interface BankrollDetailsRequest {
  bankroll: number;
  kelly_multiple: number;
  /**
   * The arbitrage stake (USD) — the size of each arb the user wants to
   * place. Kept entirely separate from the +EV `bankroll` above so the
   * arb scanner never sizes off the EV bankroll. Snake_case to match the
   * backend's SNAKE_CASE Jackson strategy.
   */
  arb_stake?: number;
}

interface BankrollFormProps {
  show: boolean;
}

export const bankrollSignal = signal<BankrollDetailsRequest>(
  {} as BankrollDetailsRequest
);

/**
 * The user's saved arbitrage stake, decoupled from the EV `bankrollSignal`.
 * Populated from the same `/bankroll` response but read independently by
 * the arb scanner so changing the EV bankroll never moves arb results.
 */
export const arbStakeSignal = signal<{ arb_stake?: number }>({});

export const getBankRollData = async (): Promise<void> => {
  const url = "api/v1/users/bankroll";
  try {
    const response = await getRequest(url);
    if (response && response.data) {
      bankrollSignal.value = response.data;
      arbStakeSignal.value = { arb_stake: response.data.arb_stake };
    } else {
      bankrollSignal.value = {} as BankrollDetailsRequest;
      arbStakeSignal.value = {};
    }
  } catch (error) {
    console.error(error);
    bankrollSignal.value = {} as BankrollDetailsRequest;
    arbStakeSignal.value = {};
  }
};

/**
 * Persist the arbitrage stake only. The backend leaves the EV bankroll
 * fields untouched when they're absent from the payload, so this never
 * disturbs EV sizing. Best-effort: no-op for signed-out users (the POST
 * 404s and we swallow it). Updates {@link arbStakeSignal} optimistically
 * so the value survives a reload.
 */
export const saveArbStake = async (arbStake: number): Promise<void> => {
  try {
    await postRequest("/api/v1/users/bankroll", { arb_stake: arbStake });
    arbStakeSignal.value = { arb_stake: arbStake };
  } catch (error) {
    console.error("Failed to persist arb stake", error);
  }
};

/** Apply EV bankroll + Kelly locally (does not persist). */
export function applyBankrollValues(bankroll: number, kelly_multiple: number): void {
  bankrollSignal.value = {
    ...bankrollSignal.value,
    bankroll,
    kelly_multiple,
  };
}

/** Persist the current EV bankroll signal; returns false on failure. */
export async function persistBankrollSignal(): Promise<boolean> {
  try {
    const response = await postRequest("/api/v1/users/bankroll", bankrollSignal.value);
    if (response?.bankroll != null) {
      bankrollSignal.value = response;
      return true;
    }
  } catch (error) {
    console.error("Failed to persist bankroll", error);
  }
  return false;
}

/**
 * Persist the EV bankroll + Kelly multiplier without reloading the page.
 * Used by the parlay builder quiz flow so mid-flow state is not lost.
 */
export const saveBankroll = async (
  bankroll: number,
  kellyMultiple: number
): Promise<void> => {
  const kelly = Math.max(0.1, Math.min(1.0, kellyMultiple || 0.25));
  bankrollSignal.value = { ...bankrollSignal.value, bankroll, kelly_multiple: kelly };
  try {
    await postRequest("/api/v1/users/bankroll", {
      bankroll,
      kelly_multiple: kelly,
    });
  } catch (error) {
    console.error("Failed to persist bankroll", error);
  }
};

 //getBankRollData();

const BankrollForm: React.FC<BankrollFormProps> = ({ show }) => {
  const bankrollValue = useSignalValue(bankrollSignal);

  const [error, setError] = useState<string>("");

  useEffect(() => {
    getBankRollData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = parseFloat(value);

    // Using the signal to update the state
    bankrollSignal.value = {
      ...bankrollSignal.value,
      [name]: numericValue,
    };

    // Validate kelly_multiple
    if (
      name === "kelly_multiple" &&
      (numericValue < 0.1 || numericValue > 1.0)
    ) {
      setError("Kelly Multiplier must be between 0.1 and 1.0");
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const ok = await persistBankrollSignal();
    if (ok) setError("");
  };

  if (!show) return null;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", marginBottom: "20px" }}
    >
      <label htmlFor="bankroll">Bankroll</label>
      <input
        className="ph-mask"
        type="number"
        id="bankroll"
        name="bankroll"
        value={bankrollValue?.bankroll?.toString() ?? ''}
        onChange={handleInputChange}
        placeholder="Enter your bankroll"
      />

      <label htmlFor="kelly_multiple">Kelly Multiplier</label>
      <input
        className="ph-mask"
        type="number"
        id="kelly_multiple"
        name="kelly_multiple"
        min="0.1"
        max="1.0"
        step="0.1"
        value={bankrollValue?.kelly_multiple?.toString()?? ''}
        onChange={handleInputChange}
        placeholder="Enter your Kelly multiplier"
      />
      {error && <div style={{ color: "red" }}>{error}</div>}

      {!error && (
        <button
          onClick={handleSubmit}
          disabled={!!error}
          style={{ alignSelf: "flex-end", marginTop: "10px" }}
        >
          Update
        </button>
      )}
    </div>
  );
};

export default BankrollForm;
